<template>
  <div data-testid="dashboard-v1">
    <template v-if="!processing">
      <DashboardIntro
        @add-child="$router.push({ name: 'ChildNew' })"
        :add-child="hasPublishedChildSubsidies()"
        :first-name="$store.state.profile.first_name"
      />

      <DashboardInstructions :schema="groupSchema" />

      <template v-if="familySubsidyPrograms?.length > 0">
        <v-card
          class="mb-4"
          border
          flat
          tile
        >
          <v-card-text>
            <v-row
              v-for="familySubsidyProgram in familySubsidyPrograms"
              :key="familySubsidyProgram.id"
            >
              <v-col
                v-if="
                  !familySubsidyProgram.enable_invite_only ||
                  hasUnfinishedFamilySubsidies(filteredFamilySubsidies(familySubsidyProgram))
                "
              >
                <FamilySubsidyCard
                  @create="createFamilySubsidy"
                  :family-subsidies="filteredFamilySubsidies(familySubsidyProgram)"
                  :family-subsidy-program="familySubsidyProgram"
                  :processing="creatingSubsidy"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>

      <template v-if="children?.length > 0">
        <v-alert
          v-for="childSubsidyProgram in openChildSubsidyPrograms"
          :key="childSubsidyProgram.id"
          :icon="smallView ? '' : 'school'"
          color="primary"
          closable
          prominent
        >
          <v-row class="d-flex align-center">
            <v-col class="d-flex align-center">
              <p class="c-white fs-22 fw-500 mb-0">
                {{ childSubsidyProgram.name }}
              </p>
            </v-col>
            <v-col class="d-flex align-center justify-end">
              <v-btn
                :to="{
                  name: 'ChildSubsidyNew',
                  params: { subsidyProgramId: childSubsidyProgram.id },
                }"
                append-icon="east"
                class="me-4"
                color="bg-primary"
                size="x-large"
                variant="outlined"
              >
                {{ $bct('Start or continue application') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>

        <v-row>
          <v-col
            v-for="child in children"
            :key="child.id"
            cols="12"
            lg="6"
            md="6"
            sm="6"
          >
            <ChildCard :child-data="child" />
          </v-col>
        </v-row>
      </template>

      <VerticalSpacer :min-height="50" />
    </template>
  </div>
</template>

<script setup>
import ChildCard from '@/parent/components/children/ChildCard.vue';
import DashboardIntro from '@/parent/components/dashboards/DashboardIntro.vue';
import DashboardInstructions from '@/parent/components/dashboards/DashboardInstructions.vue';
import FamilySubsidyCard from '@/parent/components/family-subsidies/FamilySubsidyCard.vue';
import VerticalSpacer from '@/shared/components/VerticalSpacer.vue';
import { openSubsidyPrograms, subsidyProgramIsOpen } from '@/parent/services/subsidies';
import useApi from '@/shared/composables/useApi';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

defineProps({
  group: {
    type: Object,
    default: null,
  },
  groupSchema: {
    type: Object,
    default: null,
  },
});

const { api } = useApi();
const router = useRouter();
const store = useStore();
const $bct = inject('translator');

const children = ref(null);
const childSubsidyPrograms = ref([]);
const familySubsidies = ref([]);
const familySubsidyPrograms = ref(null);
const processing = ref(true);
const creatingSubsidy = ref(false);
const subsidies = ref(null);
const title = ref(null);

const openChildSubsidyPrograms = computed(() =>
  openSubsidyPrograms(subsidies.value, childSubsidyPrograms.value),
);
const smallView = computed(() => window.innerWidth < 400);

onMounted(async () => {
  await load();
});

async function createFamilySubsidy(familySubsidyProgram) {
  creatingSubsidy.value = true;
  const resp = await api.parent.family_subsidy.create({
    family_subsidy_program_id: familySubsidyProgram.id,
  });
  router.push({ name: 'FamilySubsidyContinue', params: { id: resp.data.id } });
}

function filteredFamilySubsidies(familySubsidyProgram) {
  return familySubsidies.value.filter(
    (subsidy) => subsidy.family_subsidy_program_id === familySubsidyProgram.id,
  );
}

function hasUnfinishedFamilySubsidies(filteredFamilySubsidies) {
  return filteredFamilySubsidies.some((subsidy) => !subsidy.submitted_at);
}

async function load() {
  processing.value = true;
  await loadFamilySubsidyPrograms();
  await loadChildren();

  if (children.value.length === 0 && familySubsidyPrograms.value.length === 0) {
    return redirectMember();
  }
  nextTick(() => title.value?.focus());

  await loadChildSubsidyPrograms();
  await loadFamilySubsidies();
  await loadSubsidies();
  processing.value = false;
  return true;
}

async function loadChildren() {
  const resp = await api.child.index();
  children.value = resp.data.filter((child) => child.name !== '');
}

async function loadFamilySubsidies() {
  const resp = await api.parent.family_subsidy.index({});
  familySubsidies.value = resp.data;
}

async function loadFamilySubsidyPrograms() {
  const resp = await api.public_api.organization.family_subsidy_program.index();
  familySubsidyPrograms.value = resp.data.filter((familySubsidyProgram) =>
    subsidyProgramIsOpen(familySubsidyProgram),
  );
}

async function loadChildSubsidyPrograms() {
  const resp = await api.public_api.organization.subsidy_program.index();
  childSubsidyPrograms.value = resp.data;
}

async function loadSubsidies() {
  const resp = await api.subsidy.index({});
  subsidies.value = resp.data;
}

function hasPublishedChildSubsidies() {
  return Object.values(childSubsidyPrograms.value).some((value) => !!value.published_at);
}

function redirectMember() {
  if (store.state.pages.Search.enabled && store.state.profile.search_id) {
    router.replace({
      name: 'SearchShow',
      params: { searchId: store.state.profile.search_id },
    });
  } else {
    router.replace({ path: '/' });
  }
}
</script>
